/**
 * Server-paginated Racks list — mirrors the legacy warehouse grid: a Status
 * filter `Select` (left) + search `Input` in a `Toolbar` over a `DataTable`
 * with a `Pagination` footer. Both `status` and `search` are pushed to the
 * backend via `useRacks`; loading → Skeleton, error → Alert+retry, empty →
 * EmptyState. Read-only (W3 scope). Rack-owned (not the shared masters section)
 * because only Racks carries the status filter.
 */
import { useEffect, useMemo, useState } from 'react';
import { Search, Server } from 'lucide-react';
import { Toolbar } from '@vnetwork/ui/layout';
import {
  Button,
  Card,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, Pagination } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { type Rack, type RackStatus, useRacks } from '@/api/capacities';
import { paginationView } from '@/pages/customers/pagination-summary';
import { rackColumns } from './list-configs/rack-column-definitions';

const PAGE_SIZE = 20;
const ALL_STATUS = '__all__';

/** Legacy status filter options — the full `RackStatus` set, title-cased. */
const STATUS_OPTIONS: { value: RackStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'deprecated', label: 'Deprecated' },
  { value: 'reserved', label: 'Reserved' },
  { value: 'maintenance', label: 'Maintenance' },
];

/** Debounce a fast-changing value (search box) to throttle server refetches. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

export interface RacksListSectionProps {
  onRowClick?: (row: Rack) => void;
}

export function RacksListSection({ onRowClick }: RacksListSectionProps) {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string>(ALL_STATUS);
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebouncedValue(rawQuery, 400);

  const params = {
    page,
    page_size: PAGE_SIZE,
    search: query || undefined,
    status: status === ALL_STATUS ? undefined : status,
  };
  const { data, isLoading, isError, isFetching, refetch } = useRacks(params);

  const rows = data?.results ?? [];
  const pagination = data?.pagination;
  const pageCount = pagination?.total_pages ?? 1;
  const total = pagination?.total ?? rows.length;

  const onSearch = (value: string) => {
    setRawQuery(value);
    setPage(1);
  };
  const onStatusChange = (value: string) => {
    setStatus(value);
    setPage(1);
  };

  const footer = useMemo(() => {
    const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);
    return (
      <Pagination
        page={page}
        pageCount={view.pageCount}
        onPageChange={setPage}
        summary={`${view.summary}${isFetching ? ' …' : ''}`}
      />
    );
  }, [page, rows.length, total, pageCount, isFetching]);

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Toolbar
        style={{ marginBottom: 0 }}
        start={
          <Select value={status} onValueChange={onStatusChange}>
            <SelectTrigger style={{ minWidth: 200 }} aria-label="Filter by status">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_STATUS}>All statuses</SelectItem>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
        end={
          <Input
            value={rawQuery}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search racks…"
            startSlot={<Search />}
            style={{ maxWidth: 320 }}
          />
        }
      />

      {isError ? (
        <Alert
          tone="danger"
          title="Failed to load racks"
          actions={
            <Button size="sm" variant="ghost" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      ) : isLoading ? (
        <Card flat>
          <div style={{ padding: 'var(--vn-space-4)', display: 'grid', gap: 'var(--vn-space-2)' }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 24 }} />
            ))}
          </div>
        </Card>
      ) : (
        <DataTable
          columns={rackColumns}
          data={rows}
          getRowId={(r) => r.id}
          onRowClick={onRowClick}
          emptyState={
            <EmptyState
              icon={<Server size={24} />}
              title="No racks found"
              description="Try adjusting your search or status filter."
            />
          }
          footer={footer}
        />
      )}
    </div>
  );
}
